import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminScope } from '@/contexts/AdminScopeContext';
import { getRoles } from '@/services/userApi';
import { Role } from '@/types/auth';
import { CreateUserData, UpdateUserData, UserListItem } from '@/types/user';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const ROLE_LABELS: Record<Role, string> = {
  role_user: 'User',
  role_admin: 'Admin',
  role_superadmin: 'Super Admin',
};

/** Admin / superadmin roles may only be created on the CenTax Filer tenant */
const ADMIN_ROLES_TENANT_ID = 'ctf';
const USER_ONLY_ROLE_OPTIONS: Array<{ value: Role; label: string }> = [
  { value: 'role_user', label: 'User' },
];

const createUserSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  firstName: z.string().min(1, 'First name is required'),
  middleName: z.string().optional(),
  lastName: z.string().min(1, 'Last name is required'),
  role: z.enum(['role_user', 'role_admin', 'role_superadmin']),
  referralCode: z.string().optional(),
});

const editUserSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  middleName: z.string().optional(),
  lastName: z.string().min(1, 'Last name is required'),
});

type CreateFormValues = z.infer<typeof createUserSchema>;
type EditFormValues = z.infer<typeof editUserSchema>;

interface UserFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateUserData | UpdateUserData) => Promise<void>;
  user?: UserListItem;
  isLoading?: boolean;
}

function allowsAdminRoles(tenantId: string | null): boolean {
  // null = request host tenant; CTF UI host is CenTax Filer
  return tenantId == null || tenantId === ADMIN_ROLES_TENANT_ID;
}

function CreateUserForm({
  onSubmit,
  onClose,
  isLoading,
}: {
  onSubmit: (data: CreateUserData) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
}) {
  const { user: currentUser } = useAuth();
  const { selectedTenantId } = useAdminScope();
  const canAssignAdminRoles = allowsAdminRoles(selectedTenantId);
  const [roleOptions, setRoleOptions] = useState(USER_ONLY_ROLE_OPTIONS);

  const form = useForm<CreateFormValues>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
      firstName: '',
      middleName: '',
      lastName: '',
      role: 'role_user',
      referralCode: '',
    },
  });

  useEffect(() => {
    if (!canAssignAdminRoles) {
      setRoleOptions(USER_ONLY_ROLE_OPTIONS);
      form.setValue('role', 'role_user');
      return;
    }

    let cancelled = false;
    getRoles()
      .then((roles) => {
        if (cancelled) return;
        const options = roles
          .filter((r): r is { value: Role; label: string } =>
            ['role_user', 'role_admin', 'role_superadmin'].includes(r.value)
          )
          .map((r) => ({ value: r.value as Role, label: r.label }));
        if (options.length > 0) {
          setRoleOptions(options);
        } else if (currentUser?.role === 'role_superadmin') {
          setRoleOptions([
            { value: 'role_user', label: 'User' },
            { value: 'role_admin', label: 'Admin' },
            { value: 'role_superadmin', label: 'Super Admin' },
          ]);
        } else {
          setRoleOptions(USER_ONLY_ROLE_OPTIONS);
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (currentUser?.role === 'role_superadmin') {
          setRoleOptions([
            { value: 'role_user', label: 'User' },
            { value: 'role_admin', label: 'Admin' },
            { value: 'role_superadmin', label: 'Super Admin' },
          ]);
        } else {
          setRoleOptions(USER_ONLY_ROLE_OPTIONS);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.role, canAssignAdminRoles, form]);

  const handleSubmit = async (values: CreateFormValues) => {
    try {
      await onSubmit({
        ...values,
        role: canAssignAdminRoles ? values.role : 'role_user',
        middleName: values.middleName?.trim() || undefined,
        referralCode: values.referralCode?.trim() || undefined,
      });
      form.reset();
    } catch {
      // Error handled by parent
    }
  };

  const showRolePicker = canAssignAdminRoles && roleOptions.length > 1;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input placeholder="John" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Doe" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {showRolePicker ? (
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {roleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}

        <FormField
          control={form.control}
          name="referralCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Referral Code (optional)</FormLabel>
              <FormControl>
                <Input placeholder="Enter referral code" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add User
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

function EditUserForm({
  user,
  onSubmit,
  onClose,
  isLoading,
}: {
  user: UserListItem;
  onSubmit: (data: UpdateUserData) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
}) {
  const form = useForm<EditFormValues>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      firstName: user.firstName,
      middleName: user.middleName ?? '',
      lastName: user.lastName,
    },
  });

  useEffect(() => {
    form.reset({
      firstName: user.firstName,
      middleName: user.middleName ?? '',
      lastName: user.lastName,
    });
  }, [user, form]);

  const handleSubmit = async (values: EditFormValues) => {
    try {
      await onSubmit({
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName?.trim() || undefined,
      });
      form.reset();
    } catch {
      // Error handled by parent
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input placeholder="John" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="middleName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Middle Name (optional)</FormLabel>
              <FormControl>
                <Input placeholder="Middle name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-1">
          <p className="text-sm font-medium leading-none">Role</p>
          <p className="text-sm text-muted-foreground">
            {ROLE_LABELS[user.role] ?? user.role}
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

export function UserForm({
  open,
  onOpenChange,
  onSubmit,
  user,
  isLoading = false,
}: UserFormProps) {
  const isEditing = !!user;

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleCreateSubmit = async (data: CreateUserData) => {
    await onSubmit(data);
    onOpenChange(false);
  };

  const handleEditSubmit = async (data: UpdateUserData) => {
    await onSubmit(data);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit User' : 'Add New User'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the user details below.'
              : 'Enter the details for the new user. An invitation email will be sent.'}
          </DialogDescription>
        </DialogHeader>

        {isEditing && user ? (
          <EditUserForm
            user={user}
            onSubmit={handleEditSubmit}
            onClose={handleClose}
            isLoading={isLoading}
          />
        ) : (
          <CreateUserForm
            onSubmit={handleCreateSubmit}
            onClose={handleClose}
            isLoading={isLoading}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
